import Big from 'big.js'
import { Currency } from 'constants/misc'
import { DecimalDTO } from 'models/misc'
import { FeeDTO } from 'models/fee'
import { getUserLocale } from 'get-user-locale'
import i18n from 'i18next'
import { isValidCurrency } from '../validators'
import { logger } from 'utils/logging'

/** Amount of fixed decimal places to format numbers into when displaying */
export const FIXED_DECIMAL_PLACES = 2

type possiblePriceType = number | string | DecimalDTO | FeeDTO | Big

/** Locale tags Intl already rejected — one Sentry event per tag, not one per rendered price */
const reportedRejectedLocales = new Set<string>()

/**
 * Builds the `Intl.NumberFormat` every price/percentage formatter renders with. Without an explicit
 * `locale` the app's own display language wins over the browser/OS locale: `getUserLocale()` reads
 * `navigator.language`, so a German site on an English laptop rendered "8.5%" beside fully German
 * copy (QA-364). The `i18next` singleton is read directly rather than through `translations/i18n`
 * so this pure utility does not drag the API/auth import cycle into every consumer and test.
 * `i18n.resolvedLanguage` is used, not `i18n.language`: the embed hands a partner-controlled `?lang`
 * to `changeLanguage` verbatim, so `language` can be any tag (`ar-EG`, `en-u-nu-arab`) that Intl
 * accepts and renders in a foreign numbering system, while `resolvedLanguage` is constrained to a
 * language that actually has translations. A candidate Intl rejects (malformed tag, see
 * `formatPlanPrice`) is reported once and skipped before falling back to the runtime default.
 */
function numberFormat(options: Intl.NumberFormatOptions, locale?: string) {
  for (const candidate of [locale, i18n.resolvedLanguage, getUserLocale()]) {
    if (!candidate) continue
    try {
      return new Intl.NumberFormat(candidate, options)
    } catch {
      if (!reportedRejectedLocales.has(candidate)) {
        reportedRejectedLocales.add(candidate)
        logger.warn('Number formatting locale rejected by Intl', undefined, {
          context: { component: 'priceFormatter', metadata: { locale: candidate } }
        })
      }
      continue
    }
  }
  return new Intl.NumberFormat(undefined, options)
}

/** Typeguard */
function priceIsFeeDTO(price: possiblePriceType): price is FeeDTO {
  return (
    (price as FeeDTO).value !== undefined
  )
}

export interface FormatPriceOptions {
  /** A whole amount shows without decimals ("€199" rather than "€199.00"); any other keeps two. */
  hideZeroCents?: boolean
}

export function formatPrice(
  price: possiblePriceType = 0,
  currency?: Currency | string | null,
  locale?: string,
  { hideZeroCents = false }: FormatPriceOptions = {}
) {
  if (priceIsFeeDTO(price)) {
    if (isValidCurrency(price.currency)) currency = price.currency
    price = price.value
  }

  if (!isValidCurrency(currency)) {
    if (typeof price === 'number') return price.toString().replace(/\s/g, ' ')
    else if (typeof price === 'string') return price.replace(/\s/g, ' ')
    else if (price instanceof Big) return price.toFixed(FIXED_DECIMAL_PLACES)
    else return price.value.replace(/\s/g, ' ')
  }

  let priceNumber: number
  if (typeof price === 'number') priceNumber = price
  else if (typeof price === 'string') priceNumber = Number(price)
  else if (price instanceof Big) priceNumber = price.round(2).toNumber()
  else priceNumber = Number(price.value)

  const digits = hideZeroCents && Number.isInteger(priceNumber) ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {}
  return numberFormat({ style: 'currency', currency, ...digits }, locale).format(priceNumber).replace(/\s/g, ' ')
}

export function formatCurrency(currency: Currency, locale?: string) {
  const price: number = 0
  const numberFormatArray = numberFormat({ style: 'currency', currency }, locale).formatToParts(price)
  const currencyObject = numberFormatArray.find(currency => currency.type === 'currency')

  if (currencyObject?.value) return currencyObject.value
  return currency
}
/**
 * Formats a percentage for display, keeping meaningful decimals and dropping trailing zeros
 * (8.5 → "8,5" in a de locale, "8.5" in an en locale; 10 → "10"). Use it for every discount /
 * rate label instead of `Math.trunc`, which silently misreported an 8.5 % organization discount
 * as "8 %" (ISSUE-3364, QA-353).
 */
export function formatPercentage(percentage: Big | number | string, locale?: string) {
  const value = percentage instanceof Big ? percentage.toNumber() : Number(percentage)
  // Intl renders NaN/Infinity literally ("Your special discount (NaN%)") — fail to "0" and report it
  if (!Number.isFinite(value)) {
    logger.warn('formatPercentage received a non-numeric value', undefined, {
      context: { component: 'priceFormatter', metadata: { percentage: String(percentage) } }
    })
    return '0'
  }
  return numberFormat({ maximumFractionDigits: 2 }, locale).format(value)
}
